import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  MutableRefObject,
} from 'react';
import SequentialModal from './sequential-modal';
import {useLatest} from '@/common-pages/hooks/useLatest';

interface ModalConfig {
  id: string;
  modals: ReactNode[];
  transitionType?: 'slide' | 'fade' | 'scale' | 'flip';
  onAllClosed?: () => void;
  closeOnClickOutside?: boolean;
  overlayClassName?: string;
  backdropBlur?: boolean;
}

interface ModalManagerContextType {
  showModal: (config: ModalConfig) => void;
  hideModal: (id?: string) => void;
  isModalVisible: boolean;
  currentModalId: MutableRefObject<string | undefined>;
}

const ModalManagerContext = createContext<ModalManagerContextType | null>(null);

export const ModalManagerProvider: React.FC<{children: ReactNode}> = ({
  children,
}) => {
  const [currentModal, setCurrentModal] = useState<ModalConfig | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const currentModalRef = useLatest(currentModal?.id);

  const showModal = useCallback((config: ModalConfig) => {
    // 如果已有弹窗显示，先关闭当前弹窗
    if (currentModalRef.current) {
      return false;
    }

    setCurrentModal(config);
    setIsVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hideModal = useCallback(
    (id?: string) => {
      if (!id || !currentModal || currentModal.id === id) {
        setIsVisible(false);
        // 延迟清理，等待动画完成
        setTimeout(() => {
          setCurrentModal(null);
        }, 300);
      }
    },
    [currentModal],
  );

  const handleAllClosed = useCallback(() => {
    setIsVisible(false);
    currentModal?.onAllClosed?.();
    // 延迟清理，等待动画完成
    setTimeout(() => {
      setCurrentModal(null);
    }, 300);
  }, [currentModal]);

  const contextValue: ModalManagerContextType = {
    showModal,
    hideModal,
    isModalVisible: isVisible,
    currentModalId: currentModalRef,
  };

  return (
    <ModalManagerContext.Provider value={contextValue}>
      {children}
      {currentModal && (
        <SequentialModal
          visible={isVisible}
          overlayClassName={currentModal.overlayClassName}
          modals={currentModal.modals}
          backdropBlur={currentModal.backdropBlur}
          transitionType={currentModal.transitionType || 'scale'}
          onAllClosed={handleAllClosed}
          closeOnClickOutside={currentModal.closeOnClickOutside}
        />
      )}
    </ModalManagerContext.Provider>
  );
};

export const useModalManager = () => {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error(
      'useModalManager must be used within a ModalManagerProvider',
    );
  }
  return context;
};
